/**
 * Per-track and per-degree completion, computed one way.
 *
 * This lived twice — once in ProgressContext for the trainee's own view, once
 * inline in the admin console — and the two drifted, which is how a trainee
 * came to show "88% average score" beside empty progress bars. Her only
 * completed module was an OPTIONAL one: correctly left out of the denominator,
 * correctly counted in her average, and nowhere on screen to explain the gap.
 *
 * So optional completions are first-class here. A track reports what was
 * required and what was done beyond it, and both views show the same numbers
 * because they call the same function.
 */

import { SEED_COURSES, modules as builtInModules } from './modules.js'
import { mergeCatalogue, courseRoster, isPublished } from './catalogue.js'
import { coursesForDegrees } from './degrees.js'
import { isCompletionStale } from './contentFreshness.js'

/**
 * Completed, and completed on the module AS IT STANDS.
 *
 * A completion recorded before the module changed does not count — the trainee
 * retakes it to reclaim the score. This is the ONE place that decides it, so
 * course progress, degree progress and every percentage built on them agree by
 * construction rather than by four functions remembering to check.
 *
 * The progress document is untouched: staleness is derived from timestamps, so
 * accepting the completions (or an admin deciding the edit was trivial) brings
 * every score back exactly as it was. See contentFreshness.js.
 */
const isCompleted = (progress, module) => {
  const entry = progress?.[String(module.id)]
  return entry?.completed === true && !isCompletionStale(module, entry)
}

/**
 * Has this trainee actually DONE anything in this module, as opposed to having
 * opened it once?
 *
 * `startModule` writes a progress document the moment a module renders, so a
 * document exists after a single curious click. That is too weak to mean
 * anything: browser-verifying the app turned up an account with a document
 * against all 24 published modules — from clicking through to check the layout
 * — which annexed every course into "your courses" and put every required
 * module into the denominator. The home page invites exactly that click, with
 * "other courses, open if you want them".
 *
 * So engagement means evidence of work: a completion, a score, a checked
 * spreadsheet, or an attempt. Opening a module still records `startedAt` for
 * the admin console's "last active", it just no longer claims the course.
 */
export function hasEngagement(entry) {
  if (!entry) return false
  return entry.completed === true ||
    !!entry.quizResults ||
    !!entry.checkResult ||
    // Submitting a spreadsheet is work even when no verdict survives: a reset
    // clears checkResult and deliberately keeps submission, so a sheet module
    // that has been reset looks untouched without this.
    !!entry.submission ||
    (Array.isArray(entry.attemptHistory) && entry.attemptHistory.length > 0) ||
    !!entry.quizState
}

/**
 * Completion for one track.
 *
 * `percent` counts required modules only — an optional module cannot help or
 * hurt it, which is the entire point of the flag. `optionalCompleted` is
 * reported separately so somebody who has done the optional install
 * walkthrough and nothing else is not shown as having done nothing.
 */
export function courseStats(progress, course, customModules = []) {
  // Accepts a course OR a bare id, because several callers only hold the id.
  // Given the course it reads that course's own ordered module list, which is
  // the only way a module belonging to two courses counts in both.
  const c = typeof course === 'string' || typeof course === 'number'
    ? { id: String(course) }
    : course
  const allPublished = courseRoster(c, mergeCatalogue(builtInModules, customModules || []))
    .filter(isPublished)
  const required = allPublished.filter((m) => !m.optional)
  const optional = allPublished.filter((m) => m.optional)

  const completed = required.filter((m) => isCompleted(progress, m)).length
  const optionalCompleted = optional.filter((m) => isCompleted(progress, m)).length

  return {
    completed,
    total: required.length,
    percent: required.length ? Math.round((completed / required.length) * 100) : 0,
    optionalCompleted,
    optionalTotal: optional.length,
    // Opened at all — used to tell "has not begun" apart from "has begun and
    // finished nothing required", which look identical in a progress bar.
    started: allPublished.some((m) => !!progress?.[String(m.id)]),
    touched: allPublished.filter((m) => !!progress?.[String(m.id)]).length,
    // Actually worked in — the stronger signal, and the one that decides
    // whether this course counts as theirs.
    engaged: allPublished.some((m) => hasEngagement(progress?.[String(m.id)])),
  }
}

/** Completion across every course a degree carries. */
export function degreeStats(progress, degreeId, degreeList, allCoursesList = SEED_COURSES, customModules = []) {
  const courseIds = coursesForDegrees([degreeId], degreeList, allCoursesList, customModules)
  const catalogue = mergeCatalogue(builtInModules, customModules || [])

  // The DISTINCT modules across the degree's courses — not the sum of the
  // per-course totals, which is what this did before.
  //
  // Summing was correct only while a module could belong to exactly one
  // course. Now that a module can sit in several, a module in two courses of
  // the SAME degree was counted twice, so a trainee who had finished
  // everything read "18 of 20". degreeRequiredModules in degrees.js already
  // deduplicated for precisely this reason; the two are consistent again.
  const seen = new Set()
  const union = []
  for (const id of courseIds) {
    const course = (allCoursesList || []).find((t) => String(t.id) === String(id)) || { id }
    for (const mod of courseRoster(course, catalogue)) {
      const key = String(mod.id)
      if (seen.has(key)) continue
      seen.add(key)
      union.push(mod)
    }
  }

  const published = union.filter(isPublished)
  const required = published.filter((m) => !m.optional)
  const optional = published.filter((m) => m.optional)
  const completed = required.filter((m) => isCompleted(progress, m)).length

  return {
    completed,
    total: required.length,
    percent: required.length ? Math.round((completed / required.length) * 100) : 0,
    optionalCompleted: optional.filter((m) => isCompleted(progress, m)).length,
    optionalTotal: optional.length,
    courseCount: courseIds.length,
  }
}

/**
 * The average score across every module they have a score for, optional ones
 * included — because a score is a score, and leaving it out would make the
 * number disagree with the drill-down that lists it.
 *
 * Returns null rather than 0 for somebody with no scores at all: nought is a
 * result, and "hasn't done any yet" is not.
 */
export function averageScore(progress) {
  const scores = Object.values(progress || {})
    .map((p) => p?.quizResults?.percentageScore)
    .filter((s) => typeof s === 'number')
  if (!scores.length) return null
  return scores.reduce((a, b) => a + b, 0) / scores.length
}

/**
 * The cohort's average quiz score: the mean over the people who have actually
 * SAT one.
 *
 * Not over everybody. A trainee who has never opened a quiz has no score, and
 * counting them as nought makes the headline a participation rate wearing a
 * score's label — with most of a cohort not yet started it reads far below what
 * anyone actually scored.
 *
 * That is the same distinction averageScore() makes when it returns null rather
 * than 0 for a single person: nought is a result, and "hasn't done any yet" is
 * not. This is that rule applied one level up, where the admin page had been
 * dividing the sum by every user on the roster.
 *
 * Returns { average, scored, total } rather than a bare number so the page can
 * say who the figure is about — a number nobody can qualify is a number nobody
 * can trust.
 */
export function cohortAverageScore(users = []) {
  const list = users || []
  const scored = list.filter((u) => (u?.scoredModules || 0) > 0)
  return {
    average: scored.length
      ? Math.round((scored.reduce((sum, u) => sum + (u.avgScore || 0), 0) / scored.length) * 100)
      : 0,
    scored: scored.length,
    total: list.length,
  }
}

/** How many modules that average is made of, so the number can be explained. */
export function scoredModuleCount(progress) {
  return Object.values(progress || {})
    .filter((p) => typeof p?.quizResults?.percentageScore === 'number').length
}

/**
 * The progress entry as it should look after a completion write FAILED.
 *
 * `saveModuleProgress` merges optimistically — state is updated before the write
 * is attempted, so the interface responds immediately — and it does not undo
 * that when the write is rejected. For most fields the lie is harmless and
 * short-lived. For a completion it is not: the entry keeps `completed: true`,
 * so the module reads as done, and it keeps a real `completedAt`, which
 * `lastCompletedAt` uses to decide that an admin's retake request has been
 * satisfied. Both for a completion the server never received.
 *
 * Restores exactly the three keys a completion writes, to whatever they held
 * before — so a RETAKE that fails to save falls back to the earlier completion
 * rather than wiping it, and a first attempt falls back to nothing. Every other
 * field is left alone, because a quiz result written moments earlier by a
 * different call is not this call's to roll back.
 */
export const COMPLETION_KEYS = ['completed', 'completedAt', 'timeSpentMs']

export function rollbackCompletion(entry = {}, prior = {}) {
  const next = { ...entry }
  for (const key of COMPLETION_KEYS) {
    if (key in prior) next[key] = prior[key]
    else delete next[key]
  }
  return next
}

